import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import refreshTokens from '@amzn/meridian-tokens/base/icon/refresh';
import TimeoutToast from '../CoachingForm/AutoSave/TimeoutToast';
import { GoalDisplay } from '../Goals/types';
import DataSource from '../../api';
import DataState from '../../Types/DataState';
import {
  GoalComment,
  AddGoalCommentResponse,
} from '../../api/GoalComments/schema';
import { canAccessAgentResourceErrKeyword } from '../ContactListening/utils/ContactListeningConstants';

import { MERIDIAN_COLORS } from '../../Util/Colors';
import {
  formatGoalToString,
  GOAL_COMMENT_SIZE_LIMIT,
  GoalPage,
  GoalTab,
  getTimestampTranslation,
} from '../Goals/helper';
import { StateContext } from '../../GlobalStateStore';
import { getFullName } from '../CaseManagement/Helper';
import { Profile } from '../../Types';
import useCustomTracking from '../../Util/useCustomTracking';
import { generateBadgeUrl } from '../Dashboard/DashboardComponents/UserBadge';

export interface GoalCommentsModalProps {
  profile: Profile;
  selectedGoal: GoalDisplay | null;
  onClose: (...args: any[]) => any;
  page: GoalPage;
  tab: GoalTab;
}

export const GoalCommentsModal: React.FC<GoalCommentsModalProps> = ({
  profile,
  selectedGoal,
  onClose,
  page,
  tab,
}) => {
  const intl = useIntl();
  const apiNaws = DataSource.getDataFeed('NAWS');
  const globalState = useContext(StateContext);

  const { inspireOptions } = globalState.state;
  const { isTm, requesterUserID, requesterUsername } = inspireOptions.data;

  const commonClickstreamData = {
    'data-csa-c-type': 'button',
    'data-csa-c-tag1': page,
    'data-csa-c-tag2': 'Goal Setting',
    'data-csa-c-tag3': `${tab == GoalTab.ASSOCIATE ? 'Associate' : 'Team'} Tab`,
    'data-csa-c-tag4': 'Goal Comments',
    'data-csa-c-profile-id': requesterUserID,
    'data-csa-c-username': requesterUsername,
    'data-csa-c-is-tm': isTm,
  };
  const [
    GoalCloseDetailsClickstreamTrackingTag,
    emitGoalCloseDetailsClickstreamTags,
  ] = useCustomTracking(
    `${page}-goal-setting-close-details-for-${selectedGoal?.userName}-${selectedGoal?.goalID}`,
    `${page}-goal-setting-close-details`,
    {
      ...commonClickstreamData,
    }
  );

  const [getGoalCommentState, setGetGoalCommentState] = useState(
    DataState.INITIALIZED
  );
  const [goalCommentData, setGoalCommentData] = useState<GoalComment[]>([]);
  const [addGoalComment, setAddGoalComment] =
    useState<AddGoalCommentResponse>();
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState(
    Date().toString()
  );

  const [addCommentValue, setAddCommentValue] = useState('');
  const [addGoalCommentState, setAddGoalCommentState] = useState(
    DataState.INITIALIZED
  );

  async function loadComments(goal: GoalDisplay): Promise<void> {
    const queryVariables = apiNaws.prepareQueryForGetGoalComments(
      goal.userID,
      goal.goalID
    );
    await apiNaws.getGoalComments(
      queryVariables,
      setGoalCommentData,
      setGetGoalCommentState
    );

    setLastRefreshTimestamp(new Date().toString());
  }

  useEffect(() => {
    if (selectedGoal) {
      loadComments(selectedGoal);
    }
    setAddCommentValue('');
  }, [selectedGoal]);

  const onClickAddCommentSendButton = async () => {
    const queryVariables = apiNaws.prepareAddGoalCommentInput(
      selectedGoal?.userID,
      selectedGoal?.goalID,
      requesterUsername,
      addCommentValue
    );

    await apiNaws.addGoalComment(
      queryVariables,
      setAddGoalComment,
      setAddGoalCommentState
    );
  };

  useEffect(() => {
    if (addGoalCommentState === DataState.LOADED) {
      if (selectedGoal) {
        loadComments(selectedGoal);
        setAddCommentValue('');
        setAddGoalCommentState(DataState.INITIALIZED);
      }
    }
  }, [addGoalCommentState]);

  return (
    <>
      <GoalCloseDetailsClickstreamTrackingTag />
      <Modal
        title={`${intl.formatMessage(
          {
            id: 'cs_perfportal_goal_comments_modal_heading',
            defaultMessage: "Comments on {userName}'s goal",
          },
          {
            userName:
              (selectedGoal?.associate ?? selectedGoal)
                ? getFullName(selectedGoal.userName, profile)
                : '',
          }
        )}: ${selectedGoal ? formatGoalToString(selectedGoal, intl) : ''}`}
        open={selectedGoal !== null}
        onClose={(...arg) => {
          onClose(...arg);
          emitGoalCloseDetailsClickstreamTags();
        }}
        data-testid='add-goal-modal'
        width={'900px'}
        bodySpacingInset='450'
        scrollContainer='viewport'
      >
        {getGoalCommentState === DataState.LOADING ? (
          <Box height='fill' width='fill' spacingInset='300'>
            <Row
              width='100%'
              widths='fill'
              alignmentHorizontal='center'
              spacingInset='500'
            >
              <Box minHeight={70}>
                <div style={{ textAlign: 'center' }}>
                  <Loader type='circular' size='large' />
                </div>
              </Box>
            </Row>
          </Box>
        ) : getGoalCommentState === DataState.ERROR ? (
          <Box height='fill' width='fill' spacingInset='300'>
            <Alert type='error'>
              <FormattedMessage
                id='cs_perfportal_error_refresh_page'
                defaultMessage='Something went wrong. Please try refreshing the page. 
                  If the problem persists, please submit a SIM ticket for us to troubleshoot.'
              />
            </Alert>
          </Box>
        ) : (
          <>
            <Box height='fill' width='fill' spacingInset='300'>
              <Box
                overflowY='hidden'
                maxHeight='700px'
                width='fill'
                spacingInset='400'
              >
                {/* refresh button */}
                <Row alignmentHorizontal='end' spacing='100'>
                  <Text alignment='end' type='b200'>
                    {intl.formatMessage({
                      id: 'cs_perfportal_coaching_comment_modal_last_updated_at',
                      defaultMessage: 'Last updated at',
                    }) +
                      ' ' +
                      getTimestampTranslation(lastRefreshTimestamp)}
                  </Text>
                  <Button
                    onClick={() =>
                      selectedGoal ? loadComments(selectedGoal) : {}
                    }
                    type='link'
                    size='small'
                    data-csa-c-slot-id={`${page}-goal-setting-refresh-comment`}
                    data-csa-c-content-id={`${page}-goal-setting-refresh-comment-for-${selectedGoal?.userName}-${selectedGoal?.goalID}`}
                    {...commonClickstreamData}
                  >
                    <Icon tokens={refreshTokens} />{' '}
                    {intl.formatMessage({
                      id: 'cs_perfportal_coaching_comment_modal_refresh',
                      defaultMessage: 'Refresh',
                    })}
                  </Button>
                </Row>

                {goalCommentData && goalCommentData!.length > 0 ? (
                  <Box
                    overflowY='scroll'
                    maxHeight='700px'
                    width='fill'
                    spacingInset='400'
                  >
                    <Column
                      width='100%'
                      alignmentHorizontal='start'
                      spacing='200'
                    >
                      {/* TODO: refactor this part into a helper function that
                      will be shared with coaching comment */}
                      {goalCommentData?.map((comment, index) => {
                        const timestampStr = getTimestampTranslation(
                          comment.timestamp
                        );

                        return (
                          <>
                            <Row
                              width='100%'
                              alignmentHorizontal='start'
                              alignmentVertical='top'
                            >
                              <Box>
                                <img
                                  alt={`${comment.userName} badge`}
                                  src={generateBadgeUrl(comment.userName)}
                                  onError={(e): void => {
                                    e.currentTarget.src = `https://pulse.amazon.com/user/${comment.userName}/badge/100x100`;
                                    e.currentTarget.onerror = null;
                                  }}
                                  style={{
                                    height: '50px',
                                    width: '50px',
                                    objectFit: 'cover',
                                    borderRadius: '50%',
                                  }}
                                />
                              </Box>
                              <Box
                                type={'fill'}
                                spacingInset='400'
                                backgroundColor={
                                  MERIDIAN_COLORS.MERIDIAN_COLOR_LIGHT_GRAY
                                }
                                width={'100%'}
                                height={'100%'}
                                minHeight={'100%'}
                              >
                                <Text type='b300' data-cy={`comment-${index}`}>
                                  <b>
                                    {getFullName(comment.userName, profile)}
                                  </b>{' '}
                                  : {comment.comment}
                                </Text>
                              </Box>
                            </Row>
                            <Row width={'100%'} alignmentHorizontal='end'>
                              <Text
                                type='b200'
                                color='secondary'
                                alignment='end'
                              >
                                {timestampStr}
                              </Text>
                            </Row>
                          </>
                        );
                      })}
                    </Column>
                  </Box>
                ) : (
                  // no comments
                  <>
                    <Text type='h200' data-testid='add-goal-title'>
                      {`${
                        selectedGoal
                          ? formatGoalToString(selectedGoal, intl)
                          : ''
                      }`}
                    </Text>
                    <Text data-testid='add-goal-subtitle'>
                      <FormattedMessage
                        id='cs_perfportal_goal_comments_subtitle'
                        defaultMessage='Add a comment to give more details about the goal, ask a question, or give an update.'
                      />
                    </Text>
                  </>
                )}
              </Box>
              <Box width='fill' spacingInset='400'>
                <Textarea
                  label={
                    intl.formatMessage({
                      id: 'cs_perfportal_add_comment',
                      defaultMessage: 'Add Comment',
                    }) + ' *'
                  }
                  value={addCommentValue}
                  data-cy='comment-input'
                  onChange={setAddCommentValue}
                  error={addCommentValue.length > GOAL_COMMENT_SIZE_LIMIT}
                  errorMessage={
                    addCommentValue.length > GOAL_COMMENT_SIZE_LIMIT
                      ? intl.formatMessage(
                          {
                            id: 'cs_perfportal_goal_comment_comment_size_error',
                            defaultMessage:
                              'Comment must be under {maxLength} characters.',
                          },
                          {
                            maxLength: GOAL_COMMENT_SIZE_LIMIT,
                          }
                        )
                      : ''
                  }
                  placeholder={intl.formatMessage({
                    id: 'cs_perfportal_goal_comment_modal_add_comment',
                    defaultMessage: 'Add a goal comment',
                  })}
                />
              </Box>
            </Box>
            <ModalFooter>
              <Row alignmentHorizontal='end'>
                {/* TODO: SAA, refactor these two buttons to be reusable in goal
            comment and coaching comment modal. */}
                <Button
                  type='secondary'
                  size='medium'
                  minWidth={150}
                  onClick={() => onClose()}
                  data-testid='close-modal-button'
                  data-csa-c-slot-id={`${page}-goal-setting-cancel-comment`}
                  data-csa-c-content-id={`${page}-goal-setting-cancel-comment-for-${selectedGoal?.userName}-${selectedGoal?.goalID}`}
                  {...commonClickstreamData}
                >
                  <FormattedMessage
                    id='cs_perfportal_cancel'
                    defaultMessage='Cancel'
                  />
                </Button>
                <Button
                  data-cy='send-comment-button'
                  type='primary'
                  size='medium'
                  minWidth={150}
                  onClick={() => onClickAddCommentSendButton()}
                  disabled={
                    addCommentValue === '' ||
                    addCommentValue.length > GOAL_COMMENT_SIZE_LIMIT ||
                    addGoalCommentState === DataState.LOADING
                  }
                  data-csa-c-slot-id={`${page}-goal-setting-add-comment`}
                  data-csa-c-content-id={`${page}-goal-setting-add-comment-for-${selectedGoal?.userName}-${selectedGoal?.goalID}`}
                  {...commonClickstreamData}
                >
                  {addGoalCommentState === DataState.LOADING ? (
                    <Loader size='small' />
                  ) : (
                    <FormattedMessage
                      id='cs_perfportal_add_comment'
                      defaultMessage='Add Comment'
                    />
                  )}
                </Button>
              </Row>
            </ModalFooter>
          </>
        )}
      </Modal>
      {/* toasts */}
      <TimeoutToast
        show={addGoalCommentState === DataState.ERROR}
        message={
          addGoalComment &&
          addGoalComment.error &&
          addGoalComment!.error!.includes(canAccessAgentResourceErrKeyword)
            ? intl.formatMessage({
                id: 'cs_perfportal_goal_comment_send_no_permission',
                defaultMessage:
                  'You do not have the permission to comment on this goal',
              })
            : intl.formatMessage({
                id: 'cs_perfportal_goal_comment_send_error',
                defaultMessage: 'System error. Unable to send comment',
              })
        }
        duration={3000}
        type='error'
        onClose={(): void => {
          setAddGoalCommentState(DataState.INITIALIZED);
        }}
      />
      <TimeoutToast
        show={
          addGoalCommentState === DataState.LOADED ||
          addGoalCommentState === DataState.SUCCESS
        }
        message={intl.formatMessage({
          id: 'cs_perfportal_goal_comment_send_success',
          defaultMessage: 'Comment successfully added to the goal',
        })}
        duration={3000}
        type='success'
        onClose={(): void => {
          setAddGoalCommentState(DataState.INITIALIZED);
        }}
      />
    </>
  );
};

export default GoalCommentsModal;
